import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { appsflyerEvents } from '../utils/appsflyerEvents';
import * as stores from '../stores';
import { TRIAL_DURATION_DAYS } from '../constants/freemium';

const STORAGE_KEY = 'bloom_subscription_state';

interface SubscriptionState {
  periodType?: 'TRIAL' | 'INTRO' | 'NORMAL';
  willRenew?: boolean;
  productIdentifier?: string;
  originalPurchaseDate?: string;
  lastChecked: string;
}

/**
 * Get plan name from product identifier
 */
const getPlanName = (productIdentifier: string): string => {
  return productIdentifier.toLowerCase().includes('annual') ? 'Annual Plan' : 'Weekly Plan';
};

/**
 * Get price for a plan name from offerings
 */
const getPriceFromOfferings = (planName: string): number => {
  const offerings = stores.Purchase.getRawState().offerings;
  if (!offerings?.current) {
    console.warn('[TRIAL TRACKING] No offerings available, using fallback price');
    return 0;
  }

  const price = planName.includes('Annual')
    ? offerings.current.annual?.product?.price
    : offerings.current.weekly?.product?.price;

  if (!price) {
    const fallback = planName.includes('Annual') ? 104 : 5;
    console.warn(`[TRIAL TRACKING] Missing price for ${planName}, using fallback: ${fallback}`);
    return fallback;
  }

  return price;
};

/** Timeout (ms) for RevenueCat network call during startup. Prevents hanging when offline. */
const CUSTOMER_INFO_TIMEOUT_MS = 3000;

/**
 * Check for subscription status changes and track lifecycle events
 * This should be called on app initialization
 */
export const checkTrialConversion = async () => {
  // Only run on native platforms (RevenueCat not available on web)
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Race the RevenueCat call against a timeout so a missing network connection
    // doesn't block the startup sequence indefinitely (see index.tsx isAppReady flow).
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error(`getCustomerInfo timed out after ${CUSTOMER_INFO_TIMEOUT_MS}ms`)),
        CUSTOMER_INFO_TIMEOUT_MS,
      );
    });
    const { customerInfo } = await Promise.race([Purchases.getCustomerInfo(), timeoutPromise]);
    clearTimeout(timeoutId!);
    const entitlement = customerInfo.entitlements.active[stores.entitlementIdentifier];

    // Load previous state from localStorage
    const previousStateJson = localStorage.getItem(STORAGE_KEY);
    const previousState: SubscriptionState | null = previousStateJson ? JSON.parse(previousStateJson) : null;

    // Current subscription state
    const currentState: SubscriptionState = {
      periodType: entitlement?.periodType as 'TRIAL' | 'INTRO' | 'NORMAL' | undefined,
      willRenew: entitlement?.willRenew,
      productIdentifier: entitlement?.productIdentifier,
      originalPurchaseDate: entitlement?.originalPurchaseDate,
      lastChecked: new Date().toISOString(),
    };

    // If no previous state, this is first run - just save current state
    if (!previousState) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
      console.log('[TRIAL TRACKING] First run, saved initial state');
      return;
    }

    // Detect trial → paid conversion
    if (previousState.periodType === 'TRIAL' && currentState.periodType === 'NORMAL' && entitlement?.isActive) {
      const planName = getPlanName(entitlement.productIdentifier);
      const price = getPriceFromOfferings(planName);

      await appsflyerEvents.subscription.trialConverted(planName, price, 'USD');
      console.log('[TRIAL TRACKING] ✅ Trial converted to paid subscription:', planName);
    }

    // Detect trial cancellation (willRenew: true → false during trial)
    if (
      previousState.periodType === 'TRIAL' &&
      currentState.periodType === 'TRIAL' &&
      previousState.willRenew === true &&
      currentState.willRenew === false &&
      entitlement?.isActive
    ) {
      const planName = getPlanName(entitlement.productIdentifier);

      // Calculate days into trial
      let daysSinceStart = 0;
      if (entitlement.originalPurchaseDate) {
        daysSinceStart = Math.floor(
          (new Date().getTime() - new Date(entitlement.originalPurchaseDate).getTime()) / (1000 * 60 * 60 * 24),
        );
      }

      await appsflyerEvents.subscription.cancelled(planName, 'user_cancelled', daysSinceStart);
      console.log('[TRIAL TRACKING] ⚠️ Trial cancelled by user:', planName, `(${daysSinceStart} days in)`);
    }

    // Detect trial expiration without conversion
    // Previous state was trial, now no active entitlement = trial expired
    if (previousState.periodType === 'TRIAL' && !entitlement?.isActive) {
      const previousProduct = previousState.productIdentifier || 'unknown';
      const planName = getPlanName(previousProduct);

      // NOTE: TRIAL_DURATION_DAYS is a static constant (currently 7) used here because
      // checkTrialConversion() is a plain async function called outside of React
      // (from index.tsx on app boot) and cannot call React hooks.
      // If RevenueCat ever configures a different trial length, this value must be updated
      // manually. A future improvement would be to persist the dynamic offerTrialDays value
      // (e.g. in localStorage at subscription start) and read it here instead.
      await appsflyerEvents.subscription.trialExpired(planName, TRIAL_DURATION_DAYS);
      console.log('[TRIAL TRACKING] ❌ Trial expired without conversion:', planName);
    }

    // Save current state for next check
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
  } catch (error) {
    console.error('[TRIAL TRACKING] Error checking subscription status:', error);
  }
};
